import React from "react";

// fetchElectricVehicleConsumption.jsx - API functions for electric vehicle consumption management

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Get all electric vehicle consumptions
export const getAllConsumptions = async () => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumptions by building ID
export const getConsumptionsByBuildingId = async (buildingId) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/building/${buildingId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get building consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumption by ID
export const getConsumptionById = async (id) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/${id}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get consumption failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumptions by user ID
export const getConsumptionsByUserId = async (userId) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/user/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get user consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumptions by apartment number
export const getConsumptionsByApartmentNumber = async (apartmentNumber) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/apartment/${apartmentNumber}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get apartment consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumptions by license plate
export const getConsumptionsByLicensePlate = async (licensePlate) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/license/${licensePlate}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get license plate consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Get consumptions by month
export const getConsumptionsByMonth = async (year, month) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/month/${year}/${month}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Get monthly consumptions failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Create new consumption
export const createConsumption = async (consumptionData) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(consumptionData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Create consumption failed`);
    }

    const result = await response.json();
    return result;
  } catch (err) {
    throw err;
  }
};

// Update consumption
export const updateConsumption = async (id, consumptionData) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...consumptionData, id })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Update consumption failed`);
    }

    return { success: true };
  } catch (err) {
    throw err;
  }
};

// Delete consumption
export const deleteConsumption = async (id) => {
  try {
    const response = await fetch(`${API_URL}/api/ElectricVehicleConsumption/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: Delete consumption failed`);
    }

    return { success: true };
  } catch (err) {
    throw err;
  }
};

// Utility function to format dates in Hebrew
export const formatDateHebrew = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Utility function to format power consumption
export const formatPowerConsumption = (consumption) => {
  return `${consumption.toFixed(2)} kWh`;
};

// Utility function to format money amounts
export const formatAmount = (amount) => {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS'
  }).format(amount);
};

// Calculate total consumption for a list of consumptions
export const calculateTotalConsumption = (consumptions) => {
  return consumptions.reduce((total, item) => total + item.powerConsumption, 0);
};

// Calculate total amount to pay for a list of consumptions
export const calculateTotalAmount = (consumptions) => {
  return consumptions.reduce((total, item) => total + item.totalMoneyNeedPay, 0);
};
